import { useContext, useEffect, useMemo, useState } from 'react';
import { DataTable, type DataTableColumn } from '../../design-system/components/DataTable';
import {
  AttentionStrip,
  ScreenTabs,
  ScreenTitleRow,
  StatusCell,
  SummaryLine,
  TableToolbar,
  type AttentionItem,
  type SummaryFact,
} from '../../design-system/components/ScreenParts';
import { StatusChip } from '../../design-system/components/StatusChip';
import { EmptyState } from '../../design-system/components/EmptyState';
import { AlertBanner } from '../../design-system/components/AlertBanner';
import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { useDeviceNotes } from '../../api/hooks/useDeviceNotes';
import { useDoorHealth, useDoors, useDoorWriteGates } from '../../api/hooks/useDoors';
import { useLifts, useLiftWriteGate } from '../../api/hooks/useLifts';
import { liftRulesOn, useLiftRules } from '../../api/hooks/useLiftRules';
import { useGateways, type EdgeGateway } from '../../api/hooks/useGateways';
import type { DoorViewModel, LiftViewModel } from '../../api/adapters';
import { AuthContext } from '../../auth/AuthContext';
import { audienceFor } from '../../auth/roles';
import { LIFT_ARM_WRITES_ROLES, LIFT_RULE_SWITCH_ROLES } from '../../auth/types';
import { useActionsOff } from '../../auth/useRolePreview';
import { DoorDetailDrawer } from './DoorDetailDrawer';
import { DoorOverrideChip } from './DoorOverridePanel';
import { useDoorOverrides } from '../../state/useDoorOverrides';
import { remainingS } from '../../state/door-overrides';
import {
  ALERT_DEEP_LINK_EVENT,
  type AlertDeepLinkKind,
} from '../../state/alert-deep-link';
import { LiftDetailDrawer } from './LiftDetailDrawer';
import { liftDispatchStatus } from './lift-dispatch-status';
import type { LiftCarView } from './lift-column-state';
import { formatClock, liftFreshnessCaveatFor, type LiftRobotFacts } from './lift-reading';
import { useLiftColumnState } from './useLiftColumnState';
import { useSiteLiftCars } from './useSiteLiftCars';
import { liftLiveLabel } from '../Twin/lift-columns';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { useActiveSite } from '../../state/useActiveSite';
import { useMinuteTick, useNowTick } from '../../state/clock';
import { useFireAlarm } from '../../state/useFireAlarm';
import type { StatusKind } from '../../design-system/tokens';
import { formatDateTime } from '../../utils/date-format';
import {
  devicesAttention,
  doorControllerCell,
  doorLastChange,
  doorStateCell,
  doorTone,
  gatewayFacts,
  gatewayName,
  gatewayStateCell,
  gatewayVersion,
  liftModeCell,
  liftRobotCell,
  liftSummary,
  liftTone,
  naturalKey,
} from './devices-rows';
import { DevicesGateControls, GateDrawer, type GateKind } from './DevicesGateControls';
import {
  liftRobotsCell,
  liftRobotsNotKnownCell,
  ruleBlockedLifts,
  ruleForLift,
  type LiftRobotsCell,
} from './lift-rules-words';
import { ShaftGlyph } from './ShaftGlyph';
import './DevicesScreen.css';
import { HelpScreenNote } from '../../help';

type Tab = 'lifts' | 'doors' | 'gateways';

const NAME_SORT = { columnId: 'name', direction: 'asc' } as const;

/** Who is offered *Recover…* on a stuck ride and *Arm…* on a card. Both only
 *  open a panel, which holds the press and its checks. */
const SUPERVISOR_ROLES = LIFT_ARM_WRITES_ROLES;

/** How a car's freshness sorts: worst first, as the gateway table. */
const LIFT_READING_RANK: Record<LiftCarView['freshness']['state'], number> = {
  lost: 0,
  absent: 1,
  checking: 2,
  off: 3,
  quiet: 4,
  live: 5,
};

/**
 * The Lifts table's Heard cell: how current this car's row is, on THIS
 * device's receipts (`useLiftCarViews`, the drawer's and the map's rule).
 *
 * It replaces "Last Update", which printed the worker's own stamp beside
 * every row: the worker re-stamps a car on every LiftState, so the column
 * looked current whatever the car was doing, and read against this
 * device's clock it was a cross-clock age. The words are the building
 * view's caption words, which are the drawer's own for a car with no
 * reading ("No reading since 14:02", "No fresh reading", "No reading",
 * "Not known: this screen is reconnecting"; copy deck v2, N9), "Checking",
 * "Not known", "Fire service", and "Live", "Quiet" or "Offline" where those add
 * nothing. Its tooltip is the car's freshness caveat, the one sentence
 * the drawer and the building view's lift line use too, in the reader's
 * words (`liftFreshnessCaveatFor`).
 */
function liftTableReading(
  view: Pick<LiftCarView, 'freshness' | 'level'>,
  row: LiftViewModel,
  gate: Parameters<typeof liftLiveLabel>[2],
  clock: (epochMs: number) => string,
): { label: string; status: StatusKind } {
  const label =
    liftLiveLabel(view, row, gate, clock) ?? LIFT_READING_WORD[view.freshness.state] ?? '—';
  const state = view.freshness.state;
  const status: StatusKind =
    label === 'Fire service'
      ? 'error'
      : state === 'live'
        ? 'active'
        : state === 'lost' || state === 'absent'
          ? 'offline'
          : 'idle';
  return { label, status };
}

/** The Heard column's word for a car drawn as reporting, by freshness,
 *  where `liftLiveLabel` (the building view's caption) adds nothing. "Off"
 *  would read like a lift someone switched off. */
const LIFT_READING_WORD: Partial<Record<LiftCarView['freshness']['state'], string>> = {
  live: 'Live',
  quiet: 'Quiet',
  off: 'Offline',
};

const TAB_GATE: Readonly<Record<Tab, GateKind | null>> = {
  lifts: 'lift',
  doors: 'door',
  gateways: null,
};

function matches(text: string, search: string): boolean {
  return text.toLowerCase().includes(search.trim().toLowerCase());
}

export function DevicesScreen() {
  const [tab, setTab] = useState<Tab>('lifts');
  // Selection is tracked by NAME, not by storing the clicked row's
  // view-model object directly -- the object is a snapshot frozen at
  // click time and would never again reflect live updates (motion,
  // door state, "Last Update", ...) for as long as the drawer stayed
  // open, even though the underlying query kept refreshing correctly.
  // `OverviewScreen.tsx` already did this the right way (name +
  // re-lookup on every render); this screen didn't.
  const [selectedDoorName, setSelectedDoorName] = useState<string | null>(null);
  const [selectedLiftName, setSelectedLiftName] = useState<string | null>(null);
  // Opened by Change…, not by the row: the panel opens on its Robot use section.
  const [liftRulesFocus, setLiftRulesFocus] = useState(false);
  const [gateDrawer, setGateDrawer] = useState<GateKind | null>(null);

  // Each tab owns its search and filters, and keeps them across a tab switch.
  const [liftSearch, setLiftSearch] = useState('');
  const [liftProblemsOnly, setLiftProblemsOnly] = useState(false);
  const [doorSearch, setDoorSearch] = useState('');
  const [doorFloor, setDoorFloor] = useState('');
  const [doorProblemsOnly, setDoorProblemsOnly] = useState(false);
  const [gatewaySearch, setGatewaySearch] = useState('');

  const doorsQuery = useDoors();
  const liftsQuery = useLifts();
  // ONE read of the site's arm state for this whole screen -- the
  // title row and every row's reading derive from this same object.
  // Reading it twice is the shape of the bug being fixed.
  const liftGateQuery = useLiftWriteGate();
  const doorGatesQuery = useDoorWriteGates();
  const selectedDoor = doorsQuery.data?.find((d) => d.name === selectedDoorName) ?? null;
  const selectedLift = liftsQuery.data?.find((l) => l.name === selectedLiftName) ?? null;
  // The site's cars with their building facts: which take robots, for the
  // headline and the Robot column, and the drawer's Guests and Robots lines.
  const liftFacts = useSiteLiftCars();
  const liftRows = liftsQuery.data;
  // A car the building data does not name, or a site with none, is read as
  // one that might take robots: it never gets to say "Not a robot lift" on
  // a guess. The drawer still gets only the real facts.
  const liftCars = useMemo<LiftRobotFacts[]>(() => {
    const known = liftFacts ?? [];
    const named = new Set(known.map((car) => car.name));
    const rest = (liftRows ?? [])
      .filter((l) => !named.has(l.name))
      .map((l): LiftRobotFacts => ({ name: l.name, use: 'between_floors' }));
    return [...known, ...rest];
  }, [liftFacts, liftRows]);
  // Each car on THIS device's receipts, the rule the drawer and both maps
  // read: floor while placed, door and motion only while live. The table
  // never shows a raw row's floor, door or motion beside it.
  const liftColumns = useLiftColumnState({
    cars: liftCars,
    lifts: liftRows,
    gate: liftGateQuery.data,
  });
  // "last HH:MM" in the site's zone; the wall clock only names the day.
  const { site: activeSite, sites } = useActiveSite();
  const liftZone = resolveSiteTimeZone(sites, activeSite?.id);
  const wallNowMs = useMinuteTick();
  const liftClock = (ms: number) =>
    formatClock(ms, liftZone.source === 'site' ? liftZone.timeZone : null, wallNowMs);
  // "1 min 40 s" and "10 s ago" stay current between live updates.
  const nowMs = useNowTick(5_000);
  // The tooltip words are the reader's (copy deck v2, N8): `site_supervisor`
  // and `service_operator` open this screen too. The auth CONTEXT, as
  // `ModelCheck` reads it: no provider is a site reader.
  const auth = useContext(AuthContext);
  const audience = audienceFor(auth?.roles);
  const isSupervisor = auth?.roles?.some((role) => SUPERVISOR_ROLES.includes(role)) === true;
  const actionsOff = useActionsOff();
  const mayChangeRules =
    auth?.roles?.some((role) => LIFT_RULE_SWITCH_ROLES.includes(role)) === true && !actionsOff;
  // ONE read of the lift rules for the whole table.
  const liftRules = useLiftRules();
  // Off, or not known yet: the table is as it was before lift rules, widths too.
  const rulesOn = liftRulesOn(liftRules.state);
  const ruleWho = { mySub: auth?.user?.id ?? null, myEmail: auth?.user?.email ?? null };
  // Manual-open overrides this console declared. Read once for the
  // whole screen so the table's chip and the drawer's cannot disagree
  // -- the same rule the lift write gate follows above.
  const doorOverrides = useDoorOverrides();
  // Per-door controller liveness. A SEPARATE query, for the reason
  // `useDoors` merges the registry downstream rather than inside its
  // queryFn: the doors key is also written by the SSE overlay, so
  // anything folded in there is replaced by the next live event.
  const doorHealth = useDoorHealth();
  // Holds as the server knows them, so every computer agrees. `null`: an
  // api-server with no such read, and only this browser's own record is left.
  const deviceNotes = useDeviceNotes().data;
  const serverHolds = deviceNotes ? deviceNotes.door_holds : null;
  const { isFireAlarmActive } = useFireAlarm();

  // Deep links from the alert bell and toast stack.
  //
  // The Tasks and Fleet screens have had this listener since the
  // feature shipped; Devices never did, so a door or lift alert
  // navigated the operator to `/devices` and then STOPPED -- the right
  // screen with the wrong (or no) row selected, on the one alert class
  // where the drawer is the whole point. `navigationTargetFor` has
  // routed `door:`/`lift:` links here all along
  // (`state/alert-deep-link.ts`); nothing was listening for them.
  //
  // Switching the tab as well as selecting the row, because the screen
  // opens on `lifts`: selecting a door while the Lifts table is showing
  // would open a drawer over a table the door is not in.
  useEffect(() => {
    function onDeepLink(e: Event) {
      const detail = (e as CustomEvent<AlertDeepLinkKind>).detail;
      if (!detail) return;
      if (detail.kind === 'door') {
        // Selected by NAME even when the door is not in the current
        // snapshot: the registry merge means a door with no live state
        // still has a row, and a door alert for one that has since
        // gone quiet is exactly when the drawer matters most. An
        // unmatched name simply selects nothing, and
        // `selectedDoor` stays null.
        setTab('doors');
        setSelectedDoorName(detail.doorName);
        setSelectedLiftName(null);
      } else if (detail.kind === 'lift') {
        setTab('lifts');
        setLiftRulesFocus(false);
        setSelectedLiftName(detail.liftName);
        setSelectedDoorName(null);
      }
    }
    window.addEventListener(ALERT_DEEP_LINK_EVENT, onDeepLink);
    return () => window.removeEventListener(ALERT_DEEP_LINK_EVENT, onDeepLink);
  }, []);

  // Edge box tab. An agent on each edge box probes the bridges running
  // beside it and publishes over MQTT, the api-server subscribes and ages
  // a silent one to `unreachable`, and this reads the result. It is
  // REST-polled rather than live for the same reason Issues is: it changes
  // on the order of a bridge restart.
  const gatewaysQuery = useGateways();
  const gateways = gatewaysQuery.data ?? [];

  const lifts = liftRows ?? [];
  const doors = doorsQuery.data ?? [];
  const doorGates = doorGatesQuery.data;

  // -- Summary line ----------------------------------------------------
  const lift = liftSummary(
    liftCars,
    liftColumns,
    liftFacts !== null,
    isFireAlarmActive,
    ruleBlockedLifts(liftRules.view),
  );
  const summaryFacts: SummaryFact[] = [
    ...lift.facts,
    ...gatewayFacts(gateways),
    isFireAlarmActive
      ? { id: 'fire', label: 'Fire alarm triggered', tone: 'bad' }
      : { id: 'fire', label: 'Fire alarm clear', tone: 'ok' },
  ];

  // -- Needs attention -------------------------------------------------
  const attention: AttentionItem[] = devicesAttention({
    lifts,
    columns: liftColumns,
    doors,
    doorHealth: doorHealth.byDoor,
    liftGate: liftGateQuery.data,
    doorGates,
    wallNowMs: nowMs,
  }).map((item) => {
    const { target } = item;
    const base = { id: item.id, severity: item.severity, title: item.title, detail: item.detail };
    switch (target.kind) {
      case 'lift': {
        const open = () => {
          setTab('lifts');
          setSelectedLiftName(target.name);
          setSelectedDoorName(null);
        };
        return {
          ...base,
          onDetails: open,
          action:
            target.recover && isSupervisor
              ? { label: 'Recover…', onClick: open, testId: `lift-recover-offer-${target.name}` }
              : undefined,
        };
      }
      case 'door':
        return {
          ...base,
          onDetails: () => {
            setTab('doors');
            setSelectedDoorName(target.name);
            setSelectedLiftName(null);
          },
        };
      case 'doors':
        return {
          ...base,
          onDetails: () => {
            setTab('doors');
            setDoorProblemsOnly(true);
          },
        };
      case 'lift_gate':
      case 'door_gate': {
        const kind = target.kind === 'lift_gate' ? 'lift' : 'door';
        return {
          ...base,
          onDetails: () => setGateDrawer(kind),
          action:
            target.canArm && isSupervisor
              ? { label: 'Arm…', onClick: () => setGateDrawer(kind) }
              : undefined,
        };
      }
    }
  });

  // -- Lifts ------------------------------------------------------------
  const liftCell = (l: LiftViewModel) => {
    const column = liftColumns.get(l.name);
    const robot = liftRobotCell(l, column, nowMs);
    return {
      column,
      robot,
      mode: liftModeCell(l, column, liftClock),
      tone: liftTone(column, robot),
      heard: column ? liftTableReading(column, l, liftGateQuery.data, liftClock) : null,
    };
  };
  /** The Robots column. Null: nothing to say (no rules from this api-server, or
   *  the fleet does not report this lift). A fleet that is not reporting says so
   *  rather than showing what it last said. */
  const liftRobots = (l: LiftViewModel): LiftRobotsCell | null => {
    if (liftRules.state === 'not_reporting') return liftRobotsNotKnownCell();
    const rule = ruleForLift(liftRules.view, l.name);
    return rule ? liftRobotsCell(rule, liftClock, ruleWho) : null;
  };
  const openLiftRules = (name: string) => {
    setLiftRulesFocus(true);
    setSelectedLiftName(name);
    setSelectedDoorName(null);
  };
  const liftCarWords = (l: LiftViewModel) => {
    const level = liftColumns.get(l.name)?.level ?? null;
    if (level === null) return l.currentLevel && l.hasLiveState ? 'not current' : '—';
    // An arrow to the destination, never up or down: the car's row names no
    // direction, and a car is never placed between floors.
    return l.destinationLevel && l.destinationLevel !== level
      ? `${level} → ${l.destinationLevel}`
      : level;
  };
  const liftDoorWords = (l: LiftViewModel) => {
    if (!l.hasLiveState) return null;
    const door = liftColumns.get(l.name)?.door ?? null;
    return door === null ? 'not current' : `${door.charAt(0).toUpperCase()}${door.slice(1)}`;
  };
  const liftTableColumns: DataTableColumn<LiftViewModel>[] = [
    {
      id: 'name',
      header: 'Lift',
      width: rulesOn ? '10%' : '12%',
      cell: (l) => <strong>{l.name}</strong>,
      sortable: true,
      sortValue: (l) => naturalKey(l.name),
    },
    {
      id: 'mode',
      header: 'Mode',
      width: rulesOn ? '14%' : '21%',
      cell: (l) => {
        const { mode } = liftCell(l);
        return (
          <StatusCell
            status={mode.status}
            label={mode.label}
            small={mode.small}
            dashed={mode.dashed}
            testId={`lift-mode-${l.name}`}
          />
        );
      },
      sortable: true,
      sortValue: (l) => liftCell(l).mode.label,
    },
    ...(rulesOn
      ? ([
          {
            id: 'robots',
            header: 'Robots',
            width: '22%',
            cell: (l) => {
              const cell = liftRobots(l);
              if (!cell) return '—';
              return (
                <StatusCell
                  status={cell.status}
                  label={cell.label}
                  small={cell.small}
                  dashed={cell.dashed}
                  testId={`lift-robots-${l.name}`}
                />
              );
            },
            sortable: true,
            sortValue: (l) => liftRobots(l)?.label ?? '',
          },
        ] satisfies DataTableColumn<LiftViewModel>[])
      : []),
    {
      id: 'car',
      header: 'Car',
      width: rulesOn ? '10%' : '12%',
      cell: (l) => {
        const { robot, tone, column } = liftCell(l);
        return (
          <span className="nx-devices__car">
            <ShaftGlyph
              levels={l.levels}
              level={column?.level ?? null}
              tone={tone ? 'trouble' : robot.robot ? 'robot' : 'normal'}
            />
            <span data-testid={`lift-current-floor-${l.name}`}>{liftCarWords(l)}</span>
          </span>
        );
      },
      sortable: true,
      sortValue: (l) => naturalKey(liftColumns.get(l.name)?.level ?? ''),
    },
    {
      id: 'doors',
      header: 'Doors',
      width: rulesOn ? '7%' : '11%',
      cell: (l) => {
        const door = liftDoorWords(l);
        return (
          <span data-testid={`lift-door-${l.name}`}>{door ?? '—'}</span>
        );
      },
      sortable: true,
      sortValue: (l) => liftDoorWords(l) ?? 'zz',
    },
    {
      id: 'robot',
      header: 'Robot',
      width: rulesOn ? '15%' : '22%',
      cell: (l) => {
        const { robot } = liftCell(l);
        // The verdict's sentences name the api-server and the bridge, so
        // only ops get them on hover.
        const verdict = audience === 'ops' ? liftDispatchStatus(l, liftGateQuery.data) : null;
        return (
          <span
            className={robot.stuck ? 'nx-devices__robot--stuck' : undefined}
            title={verdict ? `${verdict.meaning} ${verdict.action}` : undefined}
            data-testid={`lift-robot-${l.name}`}
          >
            {robot.text}
          </span>
        );
      },
      sortable: true,
      sortValue: (l) => liftCell(l).robot.text,
    },
    {
      id: 'heard',
      header: 'Heard',
      width: '12%',
      cell: (l) => {
        const { heard, column } = liftCell(l);
        if (!column || !heard) return '—';
        return (
          <span
            data-testid={`lift-freshness-${l.name}`}
            title={liftFreshnessCaveatFor(column.caveat, audience) ?? undefined}
          >
            <StatusChip status={heard.status} label={heard.label} />
          </span>
        );
      },
      sortable: true,
      sortValue: (l) => {
        const state = liftColumns.get(l.name)?.freshness.state;
        return state === undefined ? -1 : LIFT_READING_RANK[state];
      },
    },
    {
      id: 'actions',
      header: '',
      headerLabel: '',
      width: '10%',
      stopsRowClick: true,
      cell: (l) => (
        <span className="nx-devices__actions">
          {liftCell(l).robot.stuck && isSupervisor && (
            <button
              type="button"
              className="nx-screen-btn nx-screen-btn--primary"
              onClick={() => setSelectedLiftName(l.name)}
              data-testid={`lift-row-recover-${l.name}`}
            >
              Recover…
            </button>
          )}
          {mayChangeRules && liftRobots(l)?.changeable && (
            <button
              type="button"
              className="nx-screen-btn"
              onClick={() => openLiftRules(l.name)}
              data-testid={`lift-row-change-${l.name}`}
            >
              Change…
            </button>
          )}
          <button
            type="button"
            className="nx-screen-btn"
            onClick={() => {
              setLiftRulesFocus(false);
              setSelectedLiftName(l.name);
            }}
          >
            Details
          </button>
        </span>
      ),
    },
  ];
  const shownLifts = lifts.filter(
    (l) =>
      matches(l.name, liftSearch) &&
      (!liftProblemsOnly || liftCell(l).tone !== null || liftRobots(l)?.tone != null),
  );

  // -- Doors ------------------------------------------------------------
  const doorHealthOf = (d: DoorViewModel) => doorHealth.byDoor?.get(d.name);
  const doorToneOf = (d: DoorViewModel) => doorTone(d, doorHealthOf(d), doorGates);
  const doorFloors = [...new Set(doors.map((d) => d.level).filter(Boolean))].sort((a, b) =>
    naturalKey(a) < naturalKey(b) ? -1 : 1,
  );
  const holdOf = (d: DoorViewModel) => serverHolds?.find((h) => h.door === d.name);
  const holdWordsOf = (d: DoorViewModel) => {
    const hold = holdOf(d);
    // A hold the clock has passed since the last read is no hold.
    if (!hold || hold.until_ms <= nowMs) return null;
    return `Until ${liftClock(hold.until_ms)}${hold.by ? ` · ${hold.by}` : ''}`;
  };
  const renderDoorState = (d: DoorViewModel) => {
    const cell = doorStateCell(d);
    // The hold says why, BESIDE the state and never in place of it. While it
    // holds, `rmf-door-state-mux` suppresses the door's own reports and
    // synthesizes MODE_OPEN, so an "Open" here may be the override talking
    // rather than the door. This browser's own countdown only where the server
    // cannot say; otherwise the Held for staff column does.
    const override = serverHolds === null ? doorOverrides.byDoor.get(d.name) : undefined;
    return (
      <span className="nx-devices__door-state">
        <StatusCell
          status={cell.status}
          label={cell.label}
          dashed={cell.dashed}
          testId={d.hasLiveState ? `door-state-${d.name}` : `door-no-data-${d.name}`}
        />
        {override && <DoorOverrideChip remaining={remainingS(override, doorOverrides.now)} />}
      </span>
    );
  };
  const doorTableColumns: DataTableColumn<DoorViewModel>[] = [
    {
      id: 'name',
      header: 'Door',
      width: '20%',
      cell: (d) => <strong>{d.name}</strong>,
      sortable: true,
      sortValue: (d) => naturalKey(d.name),
    },
    {
      id: 'level',
      header: 'Floor',
      width: '8%',
      cell: (d) => d.level || '—',
      sortable: true,
      sortValue: (d) => naturalKey(d.level || ''),
    },
    {
      id: 'state',
      header: 'State',
      width: '14%',
      cell: renderDoorState,
      sortable: true,
      sortValue: (d) => doorStateCell(d).label,
    },
    {
      id: 'change',
      header: 'Last change',
      width: '20%',
      cell: (d) => (
        <span data-testid={`door-last-change-${d.name}`}>{doorLastChange(d, audience, nowMs)}</span>
      ),
      sortable: true,
      // Newest first once sorted the other way; a door with no command last.
      sortValue: (d) => d.lastCommand?.atMs ?? null,
    },
    {
      id: 'controller',
      header: 'Controller',
      width: '14%',
      cell: (d) => {
        const cell = doorControllerCell(doorHealthOf(d));
        if (!cell) return '—';
        return (
          <span data-testid={`door-controller-${d.name}`}>
            <StatusChip status={cell.status} label={cell.label} />
          </span>
        );
      },
      sortable: true,
      sortValue: (d) => doorControllerCell(doorHealthOf(d))?.rank ?? null,
    },
    {
      id: 'held',
      header: 'Held for staff',
      width: '14%',
      cell: (d) => {
        const words = holdWordsOf(d);
        return words ? <span data-testid={`door-held-${d.name}`}>{words}</span> : '—';
      },
      sortable: true,
      sortValue: (d) => holdOf(d)?.until_ms ?? null,
    },
    {
      id: 'actions',
      header: '',
      headerLabel: '',
      width: '10%',
      stopsRowClick: true,
      cell: (d) => (
        <button type="button" className="nx-screen-btn" onClick={() => setSelectedDoorName(d.name)}>
          Details
        </button>
      ),
    },
  ];
  const shownDoors = doors.filter(
    (d) =>
      matches(d.name, doorSearch) &&
      (doorFloor === '' || d.level === doorFloor) &&
      (!doorProblemsOnly || doorToneOf(d) !== null),
  );

  // -- Edge box ---------------------------------------------------------
  const gatewayDetail = (g: EdgeGateway) =>
    Object.entries(g.detail ?? {})
      .filter(([key]) => key !== 'version')
      .map(([k, v]) => `${k}=${String(v)}`)
      .join(', ');
  const gatewayTableColumns: DataTableColumn<EdgeGateway>[] = [
    {
      id: 'name',
      header: 'Service',
      width: '30%',
      cell: (g) => (
        <>
          <strong>{gatewayName(g.service)}</strong>
          {/* Ids and vendors for ops; a hotel reader has no use for them. */}
          {audience === 'ops' && (
            <span className="nx-cell-small">
              {[g.service, g.vendor, g.adapter].filter(Boolean).join(' · ')}
            </span>
          )}
        </>
      ),
      sortable: true,
      sortValue: (g) => naturalKey(gatewayName(g.service)),
    },
    {
      id: 'version',
      header: 'Version',
      width: '14%',
      cell: (g) => gatewayVersion(g) ?? '—',
      sortable: true,
      sortValue: (g) => gatewayVersion(g) ?? '',
    },
    {
      id: 'state',
      header: 'State',
      width: '30%',
      cell: (g) => {
        const cell = gatewayStateCell(g.state);
        const detail = gatewayDetail(g);
        return (
          <StatusCell
            status={cell.status}
            label={cell.label}
            // What the box said about itself is the difference between "the
            // bridge is up" and "up, with its OPC translator dead". Shown in
            // full and wrapped: a `title` never appears on a touch device.
            small={
              audience === 'ops' && detail ? (
                <span data-testid={`gateway-detail-${g.service}`}>{detail}</span>
              ) : null
            }
            testId={`gateway-state-${g.service}`}
          />
        );
      },
      sortable: true,
      sortValue: (g) => gatewayStateCell(g.state).rank,
    },
    {
      id: 'heard',
      header: 'Last heard',
      width: '26%',
      cell: (g) => formatDateTime(new Date(g.last_seen_ms).toISOString()),
      sortable: true,
      sortValue: (g) => g.last_seen_ms,
    },
  ];
  const shownGateways = gateways.filter((g) => matches(gatewayName(g.service), gatewaySearch));

  const filterEmpty = (
    <EmptyState title="Nothing matches" description="Clear the search or the filters to see the full list." />
  );

  return (
    <div className="nx-devices">
      <ScreenTitleRow title="Devices">
        <DevicesGateControls kind={TAB_GATE[tab]} onOpen={setGateDrawer} />
      </ScreenTitleRow>
      <HelpScreenNote screen="devices" />

      <SummaryLine
        headline={lift.headline}
        facts={summaryFacts}
        label="Site systems"
        testId="devices-summary"
      />

      <AttentionStrip items={attention} />

      {/* Lifts, Doors, Edge box -- the order the site works in (sg-nomad-01,
          2026-10-22): the lifts are what an operator opens this screen for,
          so they are first and selected on arrival. */}
      <ScreenTabs<Tab>
        label="Device categories"
        testIdPrefix="devices-tab"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'lifts', label: 'Lifts', count: lifts.length },
          { id: 'doors', label: 'Doors', count: doors.length },
          { id: 'gateways', label: 'Edge box', count: gateways.length },
        ]}
      />

      {tab === 'gateways' && (
        <>
          {/* THREE different nothings, and they were one before: a
              request that has not finished, a request that failed, and
              a site with no edge box. The last is the normal state of
              most sites and must not read as a fault; the middle one
              must not read as "nothing is deployed", which is the
              answer an operator would act on. */}
          {gatewaysQuery.isPending ? (
            <SkeletonLoader shape="table-row" count={3} />
          ) : gatewaysQuery.isError ? (
            <AlertBanner variant="error" title="Edge box health unavailable">
              <span data-testid="gateways-error">
                Could not read the edge box’s health for this site. This is a
                failed request, NOT a statement that nothing is running —
                whatever is on the edge box is unaffected by it.
              </span>
            </AlertBanner>
          ) : gateways.length === 0 ? (
            <EmptyState
              title="No edge box reporting"
              description="No edge box has reported here. Sites with no on-premise box show nothing, which is expected; if this site has one, call Nexsoss."
            />
          ) : (
            <>
              <TableToolbar search={gatewaySearch} onSearch={setGatewaySearch} searchLabel="Search services" />
              <DataTable
                fixedLayout
                columns={gatewayTableColumns}
                rows={shownGateways}
                getRowId={(g) => g.id}
                ariaLabel="Edge box services"
                defaultSort={NAME_SORT}
                emptyState={filterEmpty}
                rowTone={(g) => (g.state === 'up' ? null : g.state === 'down' ? 'critical' : 'warning')}
                phone={{
                  sortKeys: [
                    { id: 'name', label: 'Name' },
                    { id: 'state', label: 'State' },
                    { id: 'heard', label: 'Last heard' },
                  ],
                  renderRow: (g) => {
                    const cell = gatewayStateCell(g.state);
                    return (
                      <>
                        <span className="nx-phone-row__head">
                          <span className="nx-phone-row__name">{gatewayName(g.service)}</span>
                          <StatusChip status={cell.status} label={cell.label} />
                        </span>
                        <span className="nx-cell-small">
                          Last heard {formatDateTime(new Date(g.last_seen_ms).toISOString())}
                        </span>
                      </>
                    );
                  },
                }}
              />
            </>
          )}
        </>
      )}

      {tab === 'doors' && (
        <>
          {doorsQuery.isError && (
            <AlertBanner variant="error" title="Could not load doors">
              {String((doorsQuery.error as Error)?.message ?? 'Unknown error')}
            </AlertBanner>
          )}
          {doorsQuery.isLoading ? (
            <SkeletonLoader shape="table-row" count={3} />
          ) : doors.length === 0 ? (
            <EmptyState
              title="No doors configured"
              description="Doors registered for this site will appear here once the API returns them."
            />
          ) : (
            <>
              <TableToolbar search={doorSearch} onSearch={setDoorSearch} searchLabel="Search doors">
                <select
                  aria-label="Floor"
                  value={doorFloor}
                  onChange={(e) => setDoorFloor(e.target.value)}
                  data-testid="doors-floor-filter"
                >
                  <option value="">All floors</option>
                  {doorFloors.map((floor) => (
                    <option key={floor} value={floor}>
                      {floor}
                    </option>
                  ))}
                </select>
                <label>
                  <input
                    type="checkbox"
                    checked={doorProblemsOnly}
                    onChange={(e) => setDoorProblemsOnly(e.target.checked)}
                    data-testid="doors-problems-only"
                  />
                  Only doors with a problem
                </label>
              </TableToolbar>
              <DataTable
                fixedLayout
                columns={doorTableColumns}
                rows={shownDoors}
                getRowId={(d) => d.name}
                ariaLabel="Doors"
                onRowClick={(d) => setSelectedDoorName(d.name)}
                defaultSort={NAME_SORT}
                emptyState={filterEmpty}
                rowTone={doorToneOf}
                phone={{
                  sortKeys: [
                    { id: 'name', label: 'Name' },
                    { id: 'state', label: 'State' },
                    { id: 'level', label: 'Floor' },
                    { id: 'change', label: 'Last change' },
                  ],
                  renderRow: (d) => {
                    const cell = doorControllerCell(doorHealthOf(d));
                    return (
                      <>
                        <span className="nx-phone-row__head">
                          <span className="nx-phone-row__name">{d.name}</span>
                          {renderDoorState(d)}
                        </span>
                        <span className="nx-phone-row__fact">
                          {[d.level, doorLastChange(d, audience, nowMs)]
                            .filter((part) => part && part !== '—')
                            .join(' · ') || '—'}
                        </span>
                        {cell && (
                          <span className="nx-cell-small">Controller: {cell.label.toLowerCase()}</span>
                        )}
                        {holdWordsOf(d) && (
                          <span className="nx-cell-small">Held for staff: {holdWordsOf(d)}</span>
                        )}
                      </>
                    );
                  },
                }}
              />
            </>
          )}
        </>
      )}

      {tab === 'lifts' && (
        <>
          {liftsQuery.isError && (
            <AlertBanner variant="error" title="Could not load lifts">
              {String((liftsQuery.error as Error)?.message ?? 'Unknown error')}
            </AlertBanner>
          )}
          {liftsQuery.isLoading ? (
            <SkeletonLoader shape="table-row" count={3} />
          ) : lifts.length === 0 ? (
            <EmptyState
              title="No lifts configured"
              description="Lifts registered for this site will appear here once the API returns them."
            />
          ) : (
            <>
              <TableToolbar search={liftSearch} onSearch={setLiftSearch} searchLabel="Search lifts">
                <label>
                  <input
                    type="checkbox"
                    checked={liftProblemsOnly}
                    onChange={(e) => setLiftProblemsOnly(e.target.checked)}
                    data-testid="lifts-problems-only"
                  />
                  Only lifts with a problem
                </label>
              </TableToolbar>
              <DataTable
                fixedLayout
                columns={liftTableColumns}
                rows={shownLifts}
                getRowId={(l) => l.name}
                ariaLabel="Lifts"
                onRowClick={(l) => {
                  setLiftRulesFocus(false);
                  setSelectedLiftName(l.name);
                }}
                defaultSort={NAME_SORT}
                emptyState={filterEmpty}
                rowTone={(l) => liftCell(l).tone ?? liftRobots(l)?.tone ?? null}
                phone={{
                  sortKeys: [
                    { id: 'name', label: 'Name' },
                    { id: 'mode', label: 'Mode' },
                    ...(rulesOn ? [{ id: 'robots', label: 'Robots' }] : []),
                    { id: 'car', label: 'Floor' },
                    { id: 'heard', label: 'Last heard' },
                  ],
                  renderRow: (l) => {
                    const { mode, robot, heard } = liftCell(l);
                    const door = liftDoorWords(l);
                    const robots = liftRobots(l);
                    return (
                      <>
                        <span className="nx-phone-row__head">
                          <span className="nx-phone-row__name">{l.name}</span>
                          <StatusChip status={mode.status} label={mode.label} dashed={mode.dashed} />
                        </span>
                        <span
                          className={`nx-phone-row__fact${robot.stuck ? ' nx-devices__robot--stuck' : ''}`}
                        >
                          {robot.text}
                        </span>
                        {robots && (
                          <span
                            className={`nx-devices__robots-line${robots.blocked ? ' is-blocked' : ''}`}
                            data-testid={`lift-robots-phone-${l.name}`}
                          >
                            {[robots.label, robots.small].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        <span className="nx-cell-small">
                          {[`Car ${liftCarWords(l)}`, door && `doors ${door.toLowerCase()}`, heard?.label]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </>
                    );
                  },
                }}
              />
            </>
          )}
        </>
      )}

      {selectedDoor && (
        <DoorDetailDrawer door={selectedDoor} onClose={() => setSelectedDoorName(null)} />
      )}
      {selectedLift && (
        <LiftDetailDrawer
          // Keyed on the lift: a keyboard re-selection (DataTable Enter ->
          // onRowClick) changes the prop without a mousedown, so without a
          // key the drawer -- and its per-lift in-flight verb state -- would
          // carry over to the other lift (useOperatorRequest.ts).
          key={selectedLift.name}
          lift={selectedLift}
          onClose={() => {
            setSelectedLiftName(null);
            setLiftRulesFocus(false);
          }}
          cars={liftFacts}
          focusRules={liftRulesFocus}
        />
      )}
      {gateDrawer && <GateDrawer kind={gateDrawer} onClose={() => setGateDrawer(null)} />}
    </div>
  );
}
